'use client'

import { createStore, type StoreApi } from 'zustand/vanilla'
import { useStore } from 'zustand'
import { createContext, useContext, useRef, type ReactNode } from 'react'
import { getUserPreferences, updateUserPreferences } from '@/api/client'

const DEFAULT_FONT_SIZE = 13
const MIN_FONT_SIZE = 10
const MAX_FONT_SIZE = 24

export interface PreferencesState {
  terminalFontSize: number
  /**
   * Server-persisted last-active team, bucketed by tenant:
   * { [tenantId]: teamId }. Populated on loadPreferences; updated whenever
   * layout-store.setCurrentTeam writes a new team (via persistLastActiveTeam
   * below). Used by StoreInitializer to pick the default team on cold load /
   * after a tenant switch, falling back to fetchedTeams[0] when the persisted
   * id no longer resolves in the current tenant's teams.
   */
  lastActiveTeamByTenant: Record<string, string>

  setTerminalFontSize: (size: number) => void
  loadPreferences: () => Promise<void>
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null

// Module-level mirror of the per-tenant map. layout-store.setCurrentTeam runs
// imperatively (outside React / without access to this store's context), and
// the backend replaces the whole `lastActiveTeamByTenant` key on write (shallow
// top-level merge) — so we must always persist the COMPLETE map. This cache is
// seeded from the server (loadPreferences / StoreInitializer) and kept in sync
// on every write, so a write never drops other tenants' entries.
let lastActiveTeamCache: Record<string, string> = {}

/**
 * Seed the module cache from server-loaded preferences. Idempotent; called by
 * both loadPreferences and StoreInitializer's inline prefs fetch.
 */
export function seedLastActiveTeamCache(map: Record<string, string> | undefined | null) {
  if (map && typeof map === 'object') {
    lastActiveTeamCache = { ...map }
  }
}

/**
 * Record `teamId` as the last-active team for `tenantId` and persist the full
 * map. No-ops when the tenant is unknown or the value is unchanged (avoids
 * redundant writes on cold-load restore). Fire-and-forget.
 */
export function persistLastActiveTeam(tenantId: string | null, teamId: string) {
  if (!tenantId) return
  if (lastActiveTeamCache[tenantId] === teamId) return
  lastActiveTeamCache = { ...lastActiveTeamCache, [tenantId]: teamId }
  updateUserPreferences({ lastActiveTeamByTenant: lastActiveTeamCache }).catch((err) => {
    console.error('[PreferencesStore] failed to persist last-active team:', err)
  })
}

const createPreferencesStore = () =>
  createStore<PreferencesState>((set) => ({
    terminalFontSize: DEFAULT_FONT_SIZE,
    lastActiveTeamByTenant: {},

    setTerminalFontSize: (size: number) => {
      const clamped = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, size))
      set({ terminalFontSize: clamped })

      // Debounced API persist
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        updateUserPreferences({ terminalFontSize: clamped }).catch((err) => {
          console.error('[PreferencesStore] failed to persist preferences:', err)
        })
      }, 500)
    },

    loadPreferences: async () => {
      try {
        const prefs = await getUserPreferences()
        if (typeof prefs.terminalFontSize === 'number') {
          set({ terminalFontSize: Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, prefs.terminalFontSize)) })
        }
        const map = prefs.lastActiveTeamByTenant
        if (map && typeof map === 'object') {
          const clean = Object.fromEntries(
            Object.entries(map).filter(
              ([tid, teamId]) => typeof tid === 'string' && typeof teamId === 'string',
            ),
          ) as Record<string, string>
          set({ lastActiveTeamByTenant: clean })
          seedLastActiveTeamCache(clean)
        }
      } catch (err) {
        console.error('[PreferencesStore] failed to load preferences:', err)
      }
    },
  }))

// Context
const PreferencesStoreContext = createContext<StoreApi<PreferencesState> | null>(null)

// Provider
export function PreferencesStoreProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<StoreApi<PreferencesState>>(null)
  if (!storeRef.current) {
    storeRef.current = createPreferencesStore()
  }
  return (
    <PreferencesStoreContext value={storeRef.current}>
      {children}
    </PreferencesStoreContext>
  )
}

// Hook
export function usePreferencesStore<T>(selector: (state: PreferencesState) => T): T {
  const store = useContext(PreferencesStoreContext)
  if (!store) throw new Error('Missing PreferencesStoreProvider')
  return useStore(store, selector)
}
